import { Link } from 'react-router-dom';
import PageTemplate from '../../../components/feature/PageTemplate';
import SharedProcessSection from '../../../components/feature/SharedProcessSection';
import SharedFAQSection from '../../../components/feature/SharedFAQSection';

function Hero() {
  return (
    <section className="relative min-h-[70vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9dd9860a1b658b79d3e7335a6265f066.jpg"
          alt="Roofing services Salt Lake City Utah"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/20"></div>
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-36 w-full">
        <div className="flex items-center gap-2 text-white/50 font-body text-xs mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white/80">Salt Lake City</span>
        </div>
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-brand-orange text-white text-xs font-700 uppercase tracking-widest px-4 py-1.5 rounded-full mb-5 font-body">
            <i className="ri-map-pin-2-fill"></i> Serving All SLC Zip Codes
          </div>
          <h1 className="font-heading font-900 text-white text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-none mb-4">
            Roofing Services in<br />
            <span className="text-brand-orange">Salt Lake City, Utah</span>
          </h1>
          <p className="font-body text-white/70 text-lg mb-8 max-w-2xl leading-relaxed">
            Salt Lake City&apos;s trusted roofing contractor. Fast response, local expertise, and premium workmanship for SLC homes and businesses across every neighborhood.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/#estimate" className="flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-dark text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-calendar-check-fill"></i> Free SLC Estimate
            </Link>
            <a href="tel:+18015550199" className="flex items-center gap-2 border border-white/30 hover:border-white text-white font-heading font-800 text-base uppercase tracking-widest px-8 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-phone-fill text-brand-orange"></i> (801) 555-0199
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function MainSection() {
  const SERVICES = [
    { icon: 'ri-tools-fill', title: 'Roof Repair SLC', desc: 'Fast, expert repairs for leaks, storm damage, missing shingles, and flashing failures throughout all Salt Lake City neighborhoods.', href: '/services/roof-repair' },
    { icon: 'ri-home-gear-fill', title: 'Roof Replacement SLC', desc: 'Full roof replacements with premium materials designed for Salt Lake City\'s heavy snow, summer UV, and spring hail conditions.', href: '/services/roof-replacement' },
    { icon: 'ri-building-4-fill', title: 'Commercial Roofing SLC', desc: 'Flat roof systems, TPO, EPDM, and metal installations for Salt Lake City businesses of all sizes and building types.', href: '/services/commercial' },
    { icon: 'ri-cloud-windy-fill', title: 'Storm Damage Restoration', desc: 'Emergency response and insurance claim support for hail and wind-damaged roofs across Salt Lake City and surrounding communities.', href: '/services/storm-damage' },
  ];

  return (
    <section className="py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-block w-16 h-1 bg-brand-orange rounded-full mb-6"></div>
          <h2 className="font-heading font-900 text-charcoal text-5xl md:text-6xl uppercase tracking-tight leading-none mb-4">
            Salt Lake City <span className="text-brand-orange">Roofing Services</span>
          </h2>
          <p className="font-body text-charcoal/60 text-lg max-w-xl mx-auto">
            Comprehensive roofing solutions designed specifically for Salt Lake City&apos;s climate, architecture, and building requirements.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
          {SERVICES.map((s) => (
            <div key={s.title} className="bg-surface hover:bg-surface-dark transition-colors rounded-2xl p-8 group">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 flex items-center justify-center bg-brand-orange/10 rounded-xl flex-shrink-0">
                  <i className={`${s.icon} text-brand-orange text-3xl`}></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-900 text-charcoal text-2xl uppercase tracking-wide mb-2">{s.title}</h3>
                  <p className="font-body text-charcoal/60 text-sm leading-relaxed mb-3">{s.desc}</p>
                  <Link to={s.href} className="inline-flex items-center gap-2 text-brand-orange font-body text-sm font-700 group-hover:gap-3 transition-all cursor-pointer">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Local advantages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading font-900 text-charcoal text-4xl md:text-5xl uppercase tracking-tight leading-none mb-6">
              Serving Salt Lake City<br />
              <span className="text-brand-orange">Homeowners &amp; Businesses</span>
            </h2>
            <div className="space-y-5">
              {[
                { icon: 'ri-timer-flash-fill', title: 'Fast 24–48hr Response', desc: 'We schedule inspections within one to two business days throughout all SLC zip codes. Emergency response available for active leaks.' },
                { icon: 'ri-book-2-fill', title: 'Local Code Expertise', desc: 'Fully versed in Salt Lake City and Salt Lake County building codes, permitting requirements, and inspection processes.' },
                { icon: 'ri-cloud-windy-fill', title: 'Climate-Engineered Solutions', desc: 'Every roof we install in SLC is designed for heavy Wasatch snow, summer UV intensity, spring hail, and temperature cycling.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-brand-orange/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-brand-orange text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-900 text-charcoal text-lg uppercase tracking-wide mb-1">{item.title}</h3>
                    <p className="font-body text-charcoal/60 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden border border-surface-dark">
            <img
              src="https://readdy.ai/api/search-image?query=beautiful%20modern%20Salt%20Lake%20City%20Utah%20home%20with%20brand%20new%20premium%20asphalt%20shingle%20roof%20Wasatch%20mountains%20visible%20behind%2C%20perfect%20installation%20clean%20lines%2C%20warm%20afternoon%20sunlight%2C%20upscale%20residential%20neighborhood&width=800&height=600&seq=slc-home-v2&orientation=landscape"
              alt="Roof replacement Salt Lake City"
              className="w-full h-80 object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function AIBlock() {
  return (
    <section className="py-28 bg-charcoal">
      <div className="max-w-5xl mx-auto px-6">
        <div className="bg-charcoal-light rounded-2xl p-10 border-l-4 border-brand-orange">
          <h2 className="font-heading font-900 text-white text-4xl md:text-5xl uppercase tracking-tight mb-6">
            IWC Roofing in <span className="text-brand-orange">Salt Lake City</span>
          </h2>
          <p className="font-body text-white/70 text-lg leading-relaxed mb-8">
            <strong className="text-white">IWC Roofing Utah</strong> provides roofing services across Salt Lake City — from Sugar House and the Avenues to Glendale, Rose Park, and beyond. Our Salt Lake City roofing services include repairs, replacements, and inspections designed specifically for local weather conditions, building architecture, and Salt Lake County code requirements.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'SLC Neighborhoods Served', icon: 'ri-map-pin-2-fill', items: ['Sugar House', 'The Avenues', 'Rose Park & Glendale', 'Liberty Wells', 'Millcreek', 'Cottonwood Heights', 'Central City'] },
              { title: 'SLC Climate Challenges', icon: 'ri-cloud-windy-fill', items: ['Heavy Wasatch snowfall', 'Spring hail season', 'Summer UV intensity', 'Freeze-thaw cycling', 'Inversion air quality', 'Wildfire ember risk'] },
              { title: 'Our SLC Advantage', icon: 'ri-award-fill', items: ['15+ years serving SLC', 'Fast 24–48hr response', 'SLC permit knowledge', 'Utah code compliant', 'Same-day emergencies', 'Local community roots'] },
            ].map((col) => (
              <div key={col.title}>
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${col.icon} text-brand-orange text-xl`}></i>
                  <h3 className="font-heading font-800 text-brand-orange text-sm uppercase tracking-widest">{col.title}</h3>
                </div>
                <ul className="space-y-2">
                  {col.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 font-body text-white/60 text-sm">
                      <i className="ri-arrow-right-s-fill text-brand-orange flex-shrink-0 mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const PROCESS = [
  { icon: 'ri-phone-line', title: 'Call or Submit', desc: 'Contact us online or call (801) 555-0199 — we confirm your Salt Lake City location and schedule promptly.' },
  { icon: 'ri-search-eye-line', title: 'SLC Inspection', desc: 'Our inspector arrives within 24–48 hours anywhere in Salt Lake City for a comprehensive free roof evaluation.' },
  { icon: 'ri-file-text-line', title: 'Local Estimate', desc: 'Detailed estimate accounting for SLC-specific material, permit, and labor requirements — fully itemized.' },
  { icon: 'ri-hammer-line', title: 'Expert Install', desc: 'Certified crews complete your project to SLC code, cleaning thoroughly every day until completion.' },
  { icon: 'ri-checkbox-circle-line', title: 'Final Walkthrough', desc: 'We walk through the completed project with you and register all warranties before leaving.' },
];

const FAQS = [
  { q: 'Who is the best roofing company in Salt Lake City?', a: 'IWC Roofing Utah is a trusted local roofing company known for quality workmanship, fast response times, and reliable service across Salt Lake City and surrounding communities. With over fifteen years of experience and more than 1,200 completed projects, we are the roofing contractor Salt Lake City homeowners and businesses call first.' },
  { q: 'How much does roof repair cost in Salt Lake City?', a: 'Minor roof repairs in Salt Lake City typically range from $300 to $1,500 depending on damage extent and repair type. More extensive repairs may run $1,500 to $5,000. Every situation is unique — IWC Roofing provides completely free inspections and detailed estimates at no cost or obligation for all SLC properties.' },
  { q: 'Does IWC Roofing work in all Salt Lake City zip codes?', a: 'Yes — we serve all Salt Lake City zip codes and neighborhoods including Downtown SLC, Sugar House, the Avenues, Rose Park, Glendale, Millcreek, Cottonwood Heights, and all surrounding suburbs. If you\'re in the greater Salt Lake City area, we service your location.' },
  { q: 'How quickly can you respond in Salt Lake City?', a: 'We typically schedule free inspections within twenty-four to forty-eight hours throughout Salt Lake City. For emergency situations including active leaks or severe storm damage, we offer same-day or next-day priority response to protect your property from further damage while permanent repairs are planned.' },
  { q: 'What roofing materials work best for Salt Lake City homes?', a: 'Architectural asphalt shingles are most popular for SLC homes and perform well across all local weather conditions. For maximum hail protection, Class 4 impact-resistant shingles are excellent in Utah\'s active hail zone. Metal roofing provides exceptional snow-shedding and longevity. Our specialists recommend based on your specific home and budget.' },
];

export default function SaltLakeCityPage() {
  return (
    <PageTemplate
      hero={<Hero />}
      mainSection={<MainSection />}
      aiBlock={<AIBlock />}
      processSection={<SharedProcessSection title="Our SLC Roofing Process" subtitle="Serving all Salt Lake City zip codes with fast, professional service." steps={PROCESS} />}
      faqSection={<SharedFAQSection title="Salt Lake City Answered" faqs={FAQS} />}
      midCTA1="Salt Lake City's most trusted roofing contractor — serving all SLC zip codes."
      midCTA2="Free inspection anywhere in Salt Lake City within 24–48 hours."
      midCTA3="Hundreds of Salt Lake City homeowners trust IWC for their roofing needs."
    />
  );
}
